// frontend/src/components/settings/ConnectorsPanel.jsx
//
// Connected-systems panel, rendered as a Tile inside Store Settings (NOT in
// the left sidebar — that is reserved for business workflows). Lists the
// ConnectorInstance rows for the current store and drives the per-merchant
// connect flow:
//
//   * "Add a system" → enter the merchant's own Exact app credentials
//     (ClientID / Client Secret / Webhook Secret). Secrets are write-only.
//   * "Authorize" (per unauthorized Exact ERP row) → redirect to the
//     merchant's Exact tenant for OAuth; the callback bounces back to
//     /store-settings?exact=connected / ?exact_error=... which we surface.

import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';

import Tile from '../layout/Tile';
import StatusChip from '../common/StatusChip';
import { VENDOR_DISPLAY_NAMES } from '../../hooks/useSoR';
import { useFormatters } from '../../hooks/useFormatters';
import { useConnectors } from '../../hooks/useConnectors';
import { useInFlight } from '../../hooks/useInFlight';
import {
  useCreateExactConnector,
  useExactAuthorizeUrl,
  useExactStartSync,
} from '../../hooks/useConnectorMutations';
import ExactSupplierResolveDialog from './ExactSupplierResolveDialog';
import ExactLocationResolveDialog from './ExactLocationResolveDialog';
import ExactItemResolveDialog from './ExactItemResolveDialog';
import ExactPoResolveDialog from './ExactPoResolveDialog';
import ExactDisconnectDialog from './ExactDisconnectDialog';
import ConnectorActivityDialog from './ConnectorActivityDialog';
import { showResolveOutcome } from './resolveOutcome';
import { translateRequestError } from '../../queries/api';
import '../../styles/pages.css';
import { showToast } from '../../components/common/appToastBus';

// The three roles `ConnectorInstance.role` accepts (backend core/models.py).
const ROLE_LABEL_KEYS = {
  SALES_CHANNEL: 'connectors.roleSalesChannel',
  ERP: 'connectors.roleErp',
  WMS: 'connectors.roleWms',
};

function roleLabelKey(role) {
  const key = ROLE_LABEL_KEYS[role];
  if (key === undefined) throw new Error(`Unknown connector role: ${role}`);
  return key;
}

const STATUS_COLORS = {
  ACTIVE: 'success',
  PENDING: 'info',
  DEACTIVATED: 'neutral',
};

const EMPTY_FORM = {
  vendor: 'exact_online',
  region: 'nl',
  client_id: '',
  client_secret: '',
  webhook_secret: '',
  product_match_key: 'auto',
};

export default function ConnectorsPanel() {
  const { t } = useTranslation();
  const { formatDateTime } = useFormatters();
  const { data: rows, loading, error, refetch } = useConnectors();

  const createConnector = useCreateExactConnector();
  const authorizeUrl = useExactAuthorizeUrl();
  const startSync = useExactStartSync();

  const [dialogOpen, setDialogOpen] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [authorizingId, setAuthorizingId] = useState(null);
  const { running: syncing, track } = useInFlight();
  // Connector id whose supplier- / location- / item-Resolve wizard is open
  // (null = closed).
  const [resolveConnectorId, setResolveConnectorId] = useState(null);
  const [locationResolveConnectorId, setLocationResolveConnectorId] = useState(null);
  const [itemResolveConnectorId, setItemResolveConnectorId] = useState(null);
  const [poResolveConnectorId, setPoResolveConnectorId] = useState(null);
  // Connector id whose read-only "Details" activity dialog is open (null = closed).
  const [detailConnectorId, setDetailConnectorId] = useState(null);
  // Connector id whose Disconnect confirmation dialog is open (null = closed).
  const [disconnectConnectorId, setDisconnectConnectorId] = useState(null);

  // Surface the OAuth callback result (?exact=connected / ?exact_error=...)
  // once, then drop it from the URL so a reload does not report it again.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (!params.has('exact') && !params.has('exact_error')) return;
    if (params.get('exact') === 'connected') {
      refetch();
      showToast(t('connectors.authorizeSuccess'), 'success');
    } else {
      showToast(t('connectors.authorizeFailed'), 'error');
    }
    const url = new URL(window.location.href);
    url.searchParams.delete('exact');
    url.searchParams.delete('exact_error');
    window.history.replaceState({}, '', url);
  }, [refetch, t]);

  const handleField = (field) => (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));

  const handleAuthorize = async (connectorId) => {
    setAuthorizingId(connectorId);
    try {
      const { authorize_url: url } = await authorizeUrl.mutateAsync(connectorId);
      window.location.assign(url);
    } catch (err) {
      setAuthorizingId(null);
      showToast(translateRequestError(err, t, 'connectors.authorizeFailed'), 'error');
    }
  };

  const handleStartSync = async (connectorId) => {
    try {
      await track(connectorId, 'sync', () => startSync.mutateAsync(connectorId));
      // List refetch (in the hook) reveals the review buttons once at RESOLVE.
      showToast(t('connectors.startSyncSuccess'), 'success');
    } catch (err) {
      showToast(translateRequestError(err, t, 'connectors.startSyncFailed'), 'error');
    }
  };

  const handleSave = async () => {
    let created;
    try {
      created = await createConnector.mutateAsync({
        region: form.region,
        client_id: form.client_id.trim(),
        client_secret: form.client_secret.trim(),
        webhook_secret: form.webhook_secret.trim(),
        product_match_key: form.product_match_key,
      });
    } catch (err) {
      showToast(translateRequestError(err, t, 'connectors.addFailed'), 'error');
      return;
    }
    setDialogOpen(false);
    setForm(EMPTY_FORM);
    // Saving credentials is only step one of "connect" — continue straight
    // into the OAuth redirect so the merchant doesn't have to discover the
    // Authorize action. If the redirect can't be started, the row falls
    // back to its "Authorization required" state with a visible button.
    if (created?.id) {
      await handleAuthorize(created.id);
    }
  };

  const canSave = form.client_id.trim() && form.client_secret.trim() && !createConnector.isPending;

  const addButton = (
    <Button variant="contained" onClick={() => setDialogOpen(true)}>
      {t('connectors.addSystem')}
    </Button>
  );

  return (
    <Tile title={t('connectors.title')} headerActions={addButton}>
      <p className="connectors-panel-subtitle">
        {t('connectors.subtitle')}
      </p>

      {loading ? (
        <div className="connectors-panel-message">{t('common.loading')}</div>
      ) : error ? (
        <div className="connectors-panel-message connectors-panel-message--error">{error}</div>
      ) : rows.length === 0 ? (
        <div className="connectors-panel-message">
          {t('connectors.empty')}
        </div>
      ) : (
        <table className="connectors-table">
          <thead>
            <tr>
              <th>{t('connectors.colRole')}</th>
              <th>{t('connectors.colVendor')}</th>
              <th>{t('connectors.colStatus')}</th>
              <th>{t('connectors.colConfiguredAt')}</th>
              <th>{t('connectors.colLastSync')}</th>
              <th>{t('connectors.colLifecycle')}</th>
              <th>{t('connectors.colActions')}</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => {
              const needsAuthorize =
                row.vendor === 'exact_online'
                && row.status !== 'DEACTIVATED'
                && !row.is_authorized;
              // Parked at RESOLVE: data was pulled + staged, the merchant
              // must confirm scope before it enters planning and the
              // connector activates. pending_gates lists the gates still
              // open ('supplier_selection' / 'item_match') so each review
              // button shows only while ITS gate awaits confirmation.
              const pendingGates = row.pending_gates || [];
              const awaitingReview =
                row.vendor === 'exact_online'
                && row.current_activation?.phase === 'RESOLVE';
              const supplierGateOpen = awaitingReview && pendingGates.includes('supplier_selection');
              const locationGateOpen = awaitingReview && pendingGates.includes('location_match');
              const itemGateOpen = awaitingReview && pendingGates.includes('item_match');
              // po_match is the DEFERRED gate — injected only after the three
              // entity gates clear (so by the time it's open, they aren't).
              const poGateOpen = awaitingReview && pendingGates.includes('po_match');
              // Ongoing review: an ACTIVE connector whose nightly delta /
              // webhooks staged new or still-unlinked ERP entities — the
              // same Review wizards reopen for them.
              const stagedCounts = row.staged_counts || {};
              const activeSupplierReview =
                row.vendor === 'exact_online' && row.status === 'ACTIVE'
                && (stagedCounts.suppliers || 0) > 0;
              const activeLocationReview =
                row.vendor === 'exact_online' && row.status === 'ACTIVE'
                && (stagedCounts.locations || 0) > 0;
              const activeItemReview =
                row.vendor === 'exact_online' && row.status === 'ACTIVE'
                && (stagedCounts.items || 0) > 0;
              const activeReview = activeSupplierReview || activeLocationReview || activeItemReview;
              // Authorized (tokens stored) but staging hasn't run yet —
              // activation still parked at AUTHORIZING. The merchant clicks
              // "Start sync" to pull + stage and advance to the review gates.
              const readyToSync =
                row.vendor === 'exact_online'
                && row.is_authorized
                && row.status === 'PENDING'
                && row.current_activation?.phase === 'AUTHORIZING';
              // Authorized but in some other non-active transient state.
              const authorizedSyncPending =
                row.is_authorized && row.status === 'PENDING'
                && !awaitingReview && !readyToSync;
              return (
                <tr key={row.id}>
                  <td>{t(roleLabelKey(row.role))}</td>
                  <td>
                    {VENDOR_DISPLAY_NAMES[row.vendor] ?? row.vendor}
                  </td>
                  <td>
                    <span className="connectors-status-cell">
                      {activeReview ? (
                        // ACTIVE connector with staged updates: a single review
                        // chip replaces the status, rather than sitting beside it.
                        <StatusChip label={t('connectors.updatesToReview')} color="warning" />
                      ) : (
                        <>
                          <StatusChip
                            label={(authorizedSyncPending || awaitingReview) ? t('connectors.statusAuthorized') : row.status}
                            color={(authorizedSyncPending || awaitingReview) ? 'success' : (STATUS_COLORS[row.status] ?? 'neutral')}
                          />
                          {needsAuthorize && (
                            <StatusChip label={t('connectors.authorizeRequired')} color="warning" />
                          )}
                          {awaitingReview && (
                            <StatusChip label={t('connectors.reviewRequired')} color="warning" />
                          )}
                        </>
                      )}
                      {/* Runtime-health signal: ACTIVE + authorized but the
                          live probe failed (e.g. Exact rejects our data calls
                          after a lapsed subscription). Distinct from the
                          re-authorize state; shows alongside whatever status
                          chip is rendered above. */}
                      {row.health && (
                        <Tooltip title={t(`connectors.health.${row.health.state}Detail`)}>
                          <StatusChip
                            label={t(`connectors.health.${row.health.state}`)}
                            color={row.health.state === 'access_blocked' ? 'error' : 'warning'}
                          />
                        </Tooltip>
                      )}
                    </span>
                  </td>
                  <td>
                    {row.configured_at ? formatDateTime(row.configured_at) : '—'}
                  </td>
                  <td>
                    {row.last_sync_completed_at ? formatDateTime(row.last_sync_completed_at) : '—'}
                  </td>
                  <td>
                    {readyToSync ? (
                      <StatusChip label={t('connectors.readyToSync')} color="info" />
                    ) : awaitingReview ? (
                      <StatusChip label={t('connectors.awaitingReview')} color="warning" />
                    ) : authorizedSyncPending ? (
                      <StatusChip label={t('connectors.syncNotEnabled')} color="neutral" />
                    ) : row.current_activation ? (
                      <StatusChip label={t('connectors.connecting')} color="info" />
                    ) : row.current_deactivation ? (
                      <StatusChip label={t('connectors.disconnecting')} color="warning" />
                    ) : '—'}
                  </td>
                  <td>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, alignItems: 'center' }}>
                    {needsAuthorize ? (
                      <Button
                        variant="contained"
                        color="warning"
                        size="small"
                        onClick={() => handleAuthorize(row.id)}
                        loading={authorizingId === row.id}
                      >
                        {t('connectors.authorize')}
                      </Button>
                    ) : readyToSync ? (
                      <Button
                        variant="contained"
                        size="small"
                        onClick={() => handleStartSync(row.id)}
                        loading={row.id in syncing}
                      >
                        {t('connectors.startSync')}
                      </Button>
                    ) : awaitingReview ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {supplierGateOpen && (
                          <Button
                            variant="contained"
                            color="warning"
                            size="small"
                            onClick={() => setResolveConnectorId(row.id)}
                          >
                            {t('connectors.reviewSuppliers')}
                          </Button>
                        )}
                        {locationGateOpen && (
                          <Button
                            variant={supplierGateOpen ? 'outlined' : 'contained'}
                            color="warning"
                            size="small"
                            onClick={() => setLocationResolveConnectorId(row.id)}
                          >
                            {t('connectors.reviewLocations')}
                          </Button>
                        )}
                        {itemGateOpen && (
                          <Button
                            variant={(supplierGateOpen || locationGateOpen) ? 'outlined' : 'contained'}
                            color="warning"
                            size="small"
                            onClick={() => setItemResolveConnectorId(row.id)}
                          >
                            {t('connectors.reviewItems')}
                          </Button>
                        )}
                        {poGateOpen && (
                          <Button
                            variant={(supplierGateOpen || locationGateOpen || itemGateOpen) ? 'outlined' : 'contained'}
                            color="warning"
                            size="small"
                            onClick={() => setPoResolveConnectorId(row.id)}
                          >
                            {t('connectors.reviewPurchaseOrders')}
                          </Button>
                        )}
                      </div>
                    ) : activeReview ? (
                      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        {activeSupplierReview && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={() => setResolveConnectorId(row.id)}
                          >
                            {t('connectors.reviewSuppliers')}
                          </Button>
                        )}
                        {activeLocationReview && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={() => setLocationResolveConnectorId(row.id)}
                          >
                            {t('connectors.reviewLocations')}
                          </Button>
                        )}
                        {activeItemReview && (
                          <Button
                            variant="outlined"
                            color="warning"
                            size="small"
                            onClick={() => setItemResolveConnectorId(row.id)}
                          >
                            {t('connectors.reviewItems')}
                          </Button>
                        )}
                      </div>
                    ) : authorizedSyncPending ? (
                      <StatusChip label={t('connectors.authorized')} color="success" />
                    ) : row.is_authorized ? (
                      <StatusChip label={t('connectors.connected')} color="success" />
                    ) : '—'}
                    {row.vendor === 'exact_online' && row.status !== 'DEACTIVATED' && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setDetailConnectorId(row.id)}
                      >
                        {t('connectors.viewDetails')}
                      </Button>
                    )}
                    {row.vendor === 'exact_online' && row.status === 'ACTIVE'
                      && !row.current_deactivation && (
                      <Button
                        variant="text"
                        color="error"
                        size="small"
                        onClick={() => setDisconnectConnectorId(row.id)}
                      >
                        {t('connectors.disconnect')}
                      </Button>
                    )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} className="dialog-form">
        <DialogTitle>{t('connectors.addDialogTitle')}</DialogTitle>
        <DialogContent>
          <p style={{ color: 'var(--color-text-secondary)', margin: 0 }}>
            {t('connectors.addDialogHelp')}
          </p>
          <Stack spacing={2}>
            <TextField
              select
              label={t('connectors.fieldVendor')}
              value={form.vendor}
              onChange={handleField('vendor')}
              size="small"
              fullWidth
            >
              <MenuItem value="exact_online">{VENDOR_DISPLAY_NAMES.exact_online}</MenuItem>
            </TextField>
            <TextField
              select
              label={t('connectors.fieldRegion')}
              value={form.region}
              onChange={handleField('region')}
              size="small"
              fullWidth
            >
              <MenuItem value="nl">{t('connectors.regionNl')}</MenuItem>
            </TextField>
            <TextField
              label={t('connectors.fieldClientId')}
              value={form.client_id}
              onChange={handleField('client_id')}
              size="small"
              fullWidth
              autoComplete="off"
            />
            <TextField
              label={t('connectors.fieldClientSecret')}
              value={form.client_secret}
              onChange={handleField('client_secret')}
              size="small"
              fullWidth
              type="password"
              autoComplete="new-password"
            />
            <TextField
              label={t('connectors.fieldWebhookSecret')}
              value={form.webhook_secret}
              onChange={handleField('webhook_secret')}
              size="small"
              fullWidth
              type="password"
              autoComplete="new-password"
              helperText={t('connectors.fieldWebhookSecretHelp')}
            />
            <TextField
              select
              label={t('connectors.fieldMatchKey')}
              value={form.product_match_key}
              onChange={handleField('product_match_key')}
              size="small"
              fullWidth
              helperText={t('connectors.fieldMatchKeyHelp')}
            >
              <MenuItem value="auto">{t('connectors.matchKeyAuto')}</MenuItem>
              <MenuItem value="barcode">{t('connectors.matchKeyBarcode')}</MenuItem>
              <MenuItem value="article_code">{t('connectors.matchKeyArticleCode')}</MenuItem>
              <MenuItem value="manual">{t('connectors.matchKeyManual')}</MenuItem>
            </TextField>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)} disabled={createConnector.isPending}>
            {t('btn.cancel')}
          </Button>
          <Button variant="contained" onClick={handleSave} disabled={!canSave} loading={createConnector.isPending}>
            {t('connectors.saveCredentials')}
          </Button>
        </DialogActions>
      </Dialog>

      <ExactSupplierResolveDialog
        connectorId={resolveConnectorId}
        open={!!resolveConnectorId}
        onClose={() => setResolveConnectorId(null)}
        onConfirmed={(summary) => {
          refetch();
          showResolveOutcome(summary, t, { success: t('connectors.resolveSuccess') });
        }}
      />

      <ExactLocationResolveDialog
        connectorId={locationResolveConnectorId}
        open={!!locationResolveConnectorId}
        onClose={() => setLocationResolveConnectorId(null)}
        onConfirmed={(summary) => {
          refetch();
          showResolveOutcome(summary, t, { success: t('connectors.locationResolveSuccess') });
        }}
      />

      <ExactItemResolveDialog
        connectorId={itemResolveConnectorId}
        open={!!itemResolveConnectorId}
        onClose={() => setItemResolveConnectorId(null)}
        onConfirmed={(summary) => {
          refetch();
          const costSkipped = summary?.cost_skipped_currency?.length || 0;
          showResolveOutcome(summary, t, {
            success: t('connectors.itemResolveSuccess'),
            softWarning: costSkipped > 0
              ? t('connectors.itemResolveCostSkipped', { count: costSkipped })
              : null,
          });
        }}
      />

      <ExactPoResolveDialog
        connectorId={poResolveConnectorId}
        open={!!poResolveConnectorId}
        onClose={() => setPoResolveConnectorId(null)}
        onConfirmed={(summary) => {
          refetch();
          showResolveOutcome(summary, t, { success: t('connectors.poResolveSuccess') });
        }}
      />

      <ExactDisconnectDialog
        connectorId={disconnectConnectorId}
        open={!!disconnectConnectorId}
        onClose={() => setDisconnectConnectorId(null)}
        onConfirmed={() => {
          refetch();
          showToast(t('connectors.disconnectSuccess'), 'success');
        }}
      />

      <ConnectorActivityDialog
        connectorId={detailConnectorId}
        open={!!detailConnectorId}
        onClose={() => setDetailConnectorId(null)}
        onAuthorize={(id) => { setDetailConnectorId(null); handleAuthorize(id); }}
        onReviewSuppliers={(id) => { setDetailConnectorId(null); setResolveConnectorId(id); }}
        onReviewLocations={(id) => { setDetailConnectorId(null); setLocationResolveConnectorId(id); }}
        onReviewItems={(id) => { setDetailConnectorId(null); setItemResolveConnectorId(id); }}
      />

    </Tile>
  );
}
